import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';

const CODE_KEY = 'sp_tester_code';

function readCode() {
    try {
        return localStorage.getItem(CODE_KEY) || '';
    } catch {
        return '';
    }
}

function writeCode(code) {
    try {
        if (code) localStorage.setItem(CODE_KEY, code);
        else localStorage.removeItem(CODE_KEY);
    } catch { /* без localStorage код придётся вводить вручную */ }
}

/**
 * Закрыта ли регистрация (нет уведомления РКН) и допущен ли этот браузер как тестировщик.
 * Код доступа хранится только в localStorage: контактов мы у тестировщика не просим.
 */
export function useRegistrationGate() {
    const [loading, setLoading] = useState(true);
    const [blocked, setBlocked] = useState(false);
    const [undertakingVersion, setUndertakingVersion] = useState('1.0');
    const [testerDeclarationEnabled, setTesterDeclarationEnabled] = useState(false);
    const [code, setCode] = useState(readCode);
    const [status, setStatus] = useState(null); // Pending | Approved | Rejected | Revoked | NotFound | null
    const [usable, setUsable] = useState(false);
    const [registrationsLeft, setRegistrationsLeft] = useState(0);

    const refresh = useCallback(async (codeOverride) => {
        const api = getApiBaseUrl();
        const currentCode = codeOverride !== undefined ? codeOverride : readCode();
        try {
            const { data } = await axios.get(`${api}/auth/registration-access`);
            setBlocked(!!data.blocked);
            if (data.undertakingVersion) setUndertakingVersion(data.undertakingVersion);
            setTesterDeclarationEnabled(!!data.testerDeclarationEnabled);

            if (data.blocked && currentCode) {
                const res = await axios.post(`${api}/auth/tester-application/status`, { accessCode: currentCode });
                if (res.data.status === 'NotFound') {
                    writeCode('');
                    setCode('');
                    setStatus('NotFound');
                    setUsable(false);
                } else {
                    setStatus(res.data.status);
                    setUsable(!!res.data.usable);
                    setRegistrationsLeft(res.data.registrationsLeft ?? 0);
                }
            } else {
                setStatus(null);
                setUsable(false);
            }
        } catch {
            // Сервер недоступен: ничего не блокируем на клиенте — решение всё равно принимает бэкенд.
        }
        setLoading(false);
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    /** Подать заявку. Возвращает { ok: true, code } или { ok: false, error }. */
    const submitApplication = useCallback(async ({ nickname, comment }) => {
        try {
            const { data } = await axios.post(`${getApiBaseUrl()}/auth/tester-application`, {
                nickname,
                comment: comment || null,
                undertakingAccepted: true,
                undertakingVersion,
            });
            writeCode(data.accessCode);
            setCode(data.accessCode);
            setStatus('Pending');
            setUsable(false);
            return { ok: true, code: data.accessCode };
        } catch (err) {
            return { ok: false, error: err.response?.data?.error || 'generic' };
        }
    }, [undertakingVersion]);

    const applyCode = useCallback(async (value) => {
        const trimmed = value.trim();
        writeCode(trimmed);
        setCode(trimmed);
        await refresh(trimmed);
    }, [refresh]);

    const forgetCode = useCallback(() => {
        writeCode('');
        setCode('');
        setStatus(null);
        setUsable(false);
    }, []);

    /** Сервер ответил registration_blocked_rkn — значит флаг включили уже после загрузки страницы. */
    const markBlocked = useCallback(() => {
        setBlocked(true);
        refresh();
    }, [refresh]);

    return {
        loading,
        blocked,
        undertakingVersion,
        testerDeclarationEnabled,
        code,
        status,
        usable,
        registrationsLeft,
        /** Код, который нужно отправлять вместе с регистрацией (только если он действует). */
        testerAccessCode: blocked && usable ? code : null,
        /** Показывать блокирующее окно. */
        locked: blocked && !usable,
        refresh,
        submitApplication,
        applyCode,
        forgetCode,
        markBlocked,
    };
}
